import { UserInteractorType } from '../../../services/commands/UserInteractor';
import ContextService from '../../../services/context/ContextService';
import { PredefinedForms } from '../../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../../services/form/view-model/FormSource';
import PrintService from '../../../services/print/PrintService';
import { getCategoryState } from '../../selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetCategoryId,
} from '../../selectors/dataset';
import { getDatasetId, getNextAction } from '../../selectors/review';
import { getReviewFormData } from '../../selectors/reviewFormData';
import actions from '../../slices/actions';
import form, {
  CreatePredefinedReviewFormCreatePayload,
  FormParentType,
} from '../../slices/form';
import review from '../../slices/review';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const reviewListener = createAppListener();

const printService = new PrintService();
const contextService = new ContextService();

reviewListener.startListening(
  apiCall(
    {
      request: review.getData,
      success: review.getDataSuccess,
      failure: review.getDataFailure,
    },
    async ({ datasetId, actionName, prescript }, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasetState = filterDatasetsForCategory(state, categoryId);
      const context = await contextService.getContext(
        categoryState,
        datasetState,
        datasetId
      );

      switch (prescript.interactor.type) {
        case UserInteractorType.REVIEW_FORM: {
          const reviewData = getReviewFormData(state, datasetId);

          let data, template, variables, parseDates;
          if (reviewData.isAuto) {
            ({ data, template } = reviewData);
            variables = {
              title: reviewData.title,
              titleColor: reviewData.titleColor,
              titleTextColor: reviewData.titleTextColor,
              backgroundColor: reviewData.backgroundColor,
              textColor: reviewData.textColor,
            };
            parseDates = false;
          } else {
            const { recordViewName, recordViewMap } = reviewData;
            const { data: d, opts } =
              await printService.getReportDataFromLibraryRoutine(
                context.get('MasterCategory'),
                context.get('DisplayMode'),
                categoryState.datasets[datasetId].name,
                recordViewName,
                context.get('Id')
              );
            data = d;
            template = recordViewMap[recordViewName].TemplateCode;
            variables = opts?.variables;
            parseDates = opts?.parseDates;
          }

          return {
            command: actionName,
            datasetId,
            type: prescript.interactor.type,
            interactor: prescript.interactor,
            data,
            template,
            variables,
            parseDates,
            width: reviewData.width,
          };
        }
        case UserInteractorType.PRINT_FORM_ON_CLIENT: {
          const { data, opts } = await printService.getReportData(
            actionName,
            context.toServerContext(),
            prescript.initialArgs
          );
          return {
            type: prescript.interactor.type,
            interactor: prescript.interactor,
            data,
            template: prescript.interactor.templateName,
            variables: opts?.variables,
            events: opts?.events,
            parseDates: opts?.parseDates,
          };
        }
      }
      throw new Error(
        `Interactor "${prescript.interactor.type}" is not supposed to get report`
      );
    }
  )
);

reviewListener.startListening(
  postDispatch(review.getDataSuccess, (payload, _state, dispatch) => {
    if (payload.type === UserInteractorType.REVIEW_FORM) {
      const createComplexFormPayload: CreatePredefinedReviewFormCreatePayload =
        {
          command: payload.command,
          type: FormSourceType.PREDEFINED,
          subtype: PredefinedForms.REVIEW,
          category: payload.interactor.category,
          parent: {
            type: FormParentType.DATASET,
            id: payload.datasetId,
          },
          template: payload.template,
          values: payload.data,
          variables: payload.variables,
          parseDates: payload.parseDates,
          width: payload.width,
        };
      dispatch(form.create(createComplexFormPayload));
    } else if (payload.type === UserInteractorType.PRINT_FORM_ON_CLIENT) {
      const { data, template, variables, parseDates } = payload;
      dispatch(review.open({ data, template, variables, parseDates }));
    }
  })
);

reviewListener.startListening(
  apiCall(
    {
      request: review.close,
      success: review.closeSuccess,
      failure: review.closeFailure,
    },
    async (_payload, state, dispatch) => {
      const nextAction = getNextAction(state);
      const datasetId = getDatasetId(state);
      if (nextAction && datasetId) {
        const categoryId = getDatasetCategoryId(state, datasetId);
        dispatch(
          actions.processMultistep({
            action: nextAction,
            category: getCategoryState(state, categoryId),
            datasetId,
            datasets: state.dataset,
            prevActionResult: {},
          })
        );
      }
    }
  )
);

export default reviewListener.middleware;
